import Chart from './chart'
import {
    getCumOiDiffLineData,
    getCumStraddleLineData,
    getOiDiffLineData,
    getPcrLineData,
    getStraddleLineData,
} from './chartHelpers'
import { getMultiStrikeCallColor, resolveMultiStrikeColorPalette } from './multiStrikeColorPalettes'
import LineSeries from './series/lineSeries'
import { ChartCandle } from '../chartV2/chart.typings'
import { OverlayPriceScale, getOverlayPriceScaleStyleOptions } from '../chartV2/chartWrapper/overlay-price-scale.ts'
import { MultiStrikeChartTypes } from '../../reducer/multiStrikeChart.reducer'

const RIGHT_SCALE_PRICE_FORMAT = {
    type: 'price' as const,
    precision: 2,
    minMove: 0.01,
}

export const CUM_STRADDLE_KEY = 'cum_straddle'
export const CUM_OI_DIFF_KEY = 'cum_oi_diff'

export function straddleSnapshotKey(strike: string): string {
    return `straddle_${strike}`
}

export function oiDiffSnapshotKey(strike: string): string {
    return `oiDiff_${strike}`
}

export function pcrSnapshotKey(strike: string): string {
    return `pcr_${strike}`
}

type CombinedLegSeriesConfig = {
    chartType: Extract<MultiStrikeChartTypes, 'straddle' | 'oiDiff' | 'pcr'>
    cumKey: string
    keyPrefix: string
    snapshotKey: (strike: string) => string
    labelSuffix: string
    cumLabel: string
    /** Strike-wise PCR is one line per strike, with no total across strikes. */
    includeCumulative?: boolean
    source: 'close' | 'openInterest'
    buildStrikeLine: (
        callData: ChartCandle[] | undefined,
        putData: ChartCandle[] | undefined
    ) => Partial<ChartCandle>[]
    buildCumLine: (callLegs: ChartCandle[][], putLegs: ChartCandle[][]) => Partial<ChartCandle>[]
}

export const STRADDLE_SERIES_CONFIG: CombinedLegSeriesConfig = {
    chartType: 'straddle',
    cumKey: CUM_STRADDLE_KEY,
    keyPrefix: 'straddle_',
    snapshotKey: straddleSnapshotKey,
    labelSuffix: 'STR',
    cumLabel: 'STR',
    source: 'close',
    buildStrikeLine: getStraddleLineData,
    buildCumLine: getCumStraddleLineData,
}

export const OI_DIFF_SERIES_CONFIG: CombinedLegSeriesConfig = {
    chartType: 'oiDiff',
    cumKey: CUM_OI_DIFF_KEY,
    keyPrefix: 'oiDiff_',
    snapshotKey: oiDiffSnapshotKey,
    labelSuffix: 'DIFF',
    cumLabel: 'DIFF',
    source: 'openInterest',
    buildStrikeLine: getOiDiffLineData,
    buildCumLine: getCumOiDiffLineData,
}

export const PCR_SERIES_CONFIG: CombinedLegSeriesConfig = {
    chartType: 'pcr',
    cumKey: 'cum_pcr',
    keyPrefix: 'pcr_',
    snapshotKey: pcrSnapshotKey,
    labelSuffix: '',
    cumLabel: 'PCR',
    includeCumulative: false,
    source: 'close',
    buildStrikeLine: getPcrLineData,
    buildCumLine: () => [],
}

/**
 * Per-strike synthetic CE+PE line (straddle premium or OI diff) plus optional cumulative total.
 * Straddle and OI Diff share the same lifecycle; only keys, labels, source field and aggregators differ.
 */
export default class CombinedLegSeriesManager {
    byStrike: Record<string, LineSeries | undefined> = {}

    constructor(
        private chart: Chart,
        private config: CombinedLegSeriesConfig
    ) {}

    refresh() {
        if (this.chart.chartType !== this.config.chartType) {
            this.hideAll()
            this.hideCumulative()
            return
        }

        Object.keys(this.chart.secondarySecurities).forEach((strike, index) => {
            const legs = this.chart.secondarySecurities[strike]
            const callData = this.chart.dataManager.data[legs.callSecKey] ?? []
            const putData = this.chart.dataManager.data[legs.putSecKey] ?? []
            if (!this.byStrike[strike]) {
                const color = getMultiStrikeCallColor(this.chart.chartStyles, index, this.chart.theme)
                this.byStrike[strike] = new LineSeries(this.chart, {
                    priceScaleId: 'right',
                    color,
                    priceLineColor: color + '80',
                    visible: false,
                    ...this.chart.resolveStrikeScaleOptions(
                        this.config.labelSuffix ? `${strike} ${this.config.labelSuffix}` : strike
                    ),
                    lineType: this.chart.chartStyles.lineType,
                    lineWidth: this.chart.chartStyles.lineThickness as any,
                    lineStyle: this.chart.resolveStrikeLineStyle(),
                    crosshairMarkerRadius: 3,
                    source: this.config.source,
                    priceFormat: RIGHT_SCALE_PRICE_FORMAT,
                })
                if (this.chart.isEmbedded) {
                    this.byStrike[strike]?.seriesApi.attachPrimitive(
                        new OverlayPriceScale({
                            side: 'right',
                            ...getOverlayPriceScaleStyleOptions(this.chart.overlayMuiTheme),
                        })
                    )
                }
            }
            this.byStrike[strike]?._setData(
                this.config.buildStrikeLine(callData, putData) as ChartCandle[]
            )
        })

        Object.keys(this.byStrike).forEach((strike) => {
            if (this.chart.secondarySecurities[strike]) return
            this.byStrike[strike]?.destroy()
            delete this.byStrike[strike]
        })

        if (this.config.includeCumulative === false) {
            this.applyVisibility()
            return
        }

        const callKeys = Object.keys(this.chart.secondarySecurities).map(
            (s) => this.chart.secondarySecurities[s].callSecKey
        )
        const putKeys = Object.keys(this.chart.secondarySecurities).map(
            (s) => this.chart.secondarySecurities[s].putSecKey
        )
        const callDataArray = callKeys.map((k) => this.chart.dataManager.data[k] ?? [])
        const putDataArray = putKeys.map((k) => this.chart.dataManager.data[k] ?? [])

        if (!this.chart.dataManager.cumSeriesApis[this.config.cumKey]) {
            const palette = resolveMultiStrikeColorPalette(this.chart.chartStyles, this.chart.theme)
            this.chart.dataManager.cumSeriesApis[this.config.cumKey] = new LineSeries(this.chart, {
                priceScaleId: 'right',
                color: palette.callCumulative,
                priceLineColor: palette.callCumulative + '80',
                visible: false,
                ...this.chart.resolveStrikeScaleOptions(this.config.cumLabel),
                lineType: this.chart.chartStyles.lineType,
                lineWidth: this.chart.chartStyles.lineThickness as any,
                lineStyle: this.chart.resolveStrikeLineStyle(),
                crosshairMarkerRadius: 3,
                source: this.config.source,
                priceFormat: RIGHT_SCALE_PRICE_FORMAT,
            })
            if (this.chart.isEmbedded) {
                this.chart.dataManager.cumSeriesApis[this.config.cumKey].seriesApi.attachPrimitive(
                    new OverlayPriceScale({
                        side: 'right',
                        ...getOverlayPriceScaleStyleOptions(this.chart.overlayMuiTheme),
                    })
                )
            }
        }
        this.chart.dataManager.cumSeriesApis[this.config.cumKey]?._setData(
            this.config.buildCumLine(callDataArray, putDataArray) as ChartCandle[]
        )

        this.applyVisibility()
    }

    applyVisibility() {
        const active = this.chart.chartType === this.config.chartType
        if (!active) {
            this.hideAll()
            this.hideCumulative()
            return
        }
        if (this.config.includeCumulative === false) {
            Object.keys(this.chart.secondarySecurities).forEach((strike) => {
                const show = (this.chart.secondarySecurities[strike]?.callPut ?? []).length > 0
                this.byStrike[strike]?.updateOptions({ visible: show })
            })
            return
        }
        if (this.chart.cumOi) {
            Object.values(this.byStrike).forEach((s) => s?.updateOptions({ visible: false }))
            this.chart.dataManager.cumSeriesApis[this.config.cumKey]?.updateOptions({ visible: true })
            return
        }
        this.hideCumulative()
        Object.keys(this.chart.secondarySecurities).forEach((strike) => {
            const show = (this.chart.secondarySecurities[strike]?.callPut ?? []).length > 0
            this.byStrike[strike]?.updateOptions({ visible: show })
        })
    }

    applyPaletteColors() {
        Object.keys(this.chart.secondarySecurities).forEach((strike, index) => {
            const color = getMultiStrikeCallColor(this.chart.chartStyles, index, this.chart.theme)
            this.byStrike[strike]?.updateOptions({ color, priceLineColor: color + '80' })
        })
        const palette = resolveMultiStrikeColorPalette(this.chart.chartStyles, this.chart.theme)
        this.chart.dataManager.cumSeriesApis[this.config.cumKey]?.updateOptions({
            color: palette.callCumulative,
            priceLineColor: palette.callCumulative + '80',
        })
    }

    applyScaleAndStyleOptions() {
        const strikeLineStyle = this.chart.resolveStrikeLineStyle()
        Object.keys(this.byStrike).forEach((strike) => {
            this.byStrike[strike]?.updateOptions({
                ...this.chart.resolveStrikeScaleOptions(
                    this.config.labelSuffix ? `${strike} ${this.config.labelSuffix}` : strike
                ),
                lineType: this.chart.chartStyles.lineType,
                lineWidth: this.chart.chartStyles.lineThickness as any,
                lineStyle: strikeLineStyle,
            })
        })
        this.chart.dataManager.cumSeriesApis[this.config.cumKey]?.updateOptions({
            ...this.chart.resolveStrikeScaleOptions(this.config.cumLabel),
            lineType: this.chart.chartStyles.lineType,
            lineWidth: this.chart.chartStyles.lineThickness as any,
            lineStyle: strikeLineStyle,
        })
    }

    lineSeriesForKey(seriesKey: string): LineSeries | undefined {
        if (seriesKey === this.config.cumKey) {
            return this.chart.dataManager.cumSeriesApis[this.config.cumKey]
        }
        if (!seriesKey.startsWith(this.config.keyPrefix)) return undefined
        return this.byStrike[seriesKey.slice(this.config.keyPrefix.length)]
    }

    removeStrike(strike: string) {
        this.byStrike[strike]?.destroy()
        delete this.byStrike[strike]
    }

    destroyAll() {
        Object.values(this.byStrike).forEach((s) => s?.destroy())
        this.byStrike = {}
        if (this.config.includeCumulative === false) return
        this.chart.dataManager.cumSeriesApis[this.config.cumKey]?.destroy()
        delete this.chart.dataManager.cumSeriesApis[this.config.cumKey]
    }

    private hideAll() {
        Object.values(this.byStrike).forEach((s) => s?.updateOptions({ visible: false }))
    }

    private hideCumulative() {
        this.chart.dataManager.cumSeriesApis[this.config.cumKey]?.updateOptions({ visible: false })
    }
}
